import type { ProductInformation } from '@dealerportal/common';
import type { ComponentType } from 'react';
import { useLoadKBFrComponent } from '../../Hooks/useLoadKBFrComponent';
import { fetchCiCFeatures } from '../../Mappers/cicFeatures';
import {
  emitContextListUpdate,
  toFeatureState,
  useKBFrItemContext,
} from './kbfrItemContext';

type Props = {
  product: ProductInformation;
};

/**
 * Renders the Cyncly `ItemConfigurator` (feature/option picker) for a product.
 * Builds its own item context and, on each selection, re-resolves the product's
 * features and broadcasts the new configuration state to any mounted viewer.
 */
export const KBFrConfigurator = ({ product }: Props) => {
  const context = useKBFrItemContext(product);
  const ItemConfigurator =
    useLoadKBFrComponent<ComponentType<any>>('ItemConfigurator');

  const handleFeatureOptionSelected = async (e: IFeatureOption) => {
    if (!context) return;

    const optionId = e.id;
    // Find the feature owning the selected option, then swap its entry in the
    // configuration state for the new selection.
    const feature = product.features?.find((f) =>
      f.options.some((o) => o.id === optionId)
    );
    if (!feature) {
      console.info('KBFrConfigurator feature not found for optionId', optionId);
      return;
    }
    const newState = context.configurationState.filter(
      (s) => s.featureId !== feature.id
    );
    newState.push(toFeatureState(feature, optionId, 'user'));

    // Re-resolve the product's features against the new state so downstream
    // option availability reflects the selection.
    product.features = await fetchCiCFeatures([
      { itemId: product.productId, configurationState: newState },
    ]);
    context.configurationState = newState;
    emitContextListUpdate(context);
  };

  if (!context || !ItemConfigurator) return null;

  return (
    <div className="viewer-3d-container ui-relative ui-h-[518px]">
      <ItemConfigurator
        itemContextList={[context]}
        handleFeatureOptionSelected={handleFeatureOptionSelected}
      />
    </div>
  );
};
